import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, FileText, Loader2, Trash2, Download, Building2 } from 'lucide-react';
import { api } from '../../lib/api';

// Case documents — one file list for everyone on the case. The firm uploads
// what came in (crash report, dec page, PIP log); each treating clinic
// uploads what it produces (eval, notes, imaging, itemized bills, EOBs).
// Every org on the case sees all of it; you can only delete your own.
const fmtSize = (b) => (b == null ? '' : b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
const fmtDT = (iso) => new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

export default function CaseDocumentsTab({ orgId, caseRow, onChanged }) {
  const [docs, setDocs] = useState(null);
  const [filter, setFilter] = useState('all'); // all | mine | others
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { setDocs(await api.listCaseDocuments(orgId, caseRow.id)); setError(''); }
    catch (e) { setError(e.message); setDocs([]); }
  }, [orgId, caseRow.id]);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploading(true); setError('');
    try {
      for (const f of list) await api.uploadDocument(orgId, f, `Case ${caseRow.case_number}`, caseRow.client_id);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const open = async (d) => {
    try { const { url } = await api.getCaseDocumentUrl(orgId, caseRow.id, d.id); window.open(url, '_blank', 'noopener'); }
    catch (e) { setError(e.message); }
  };
  const remove = async (d) => {
    try { await api.deleteDocument(orgId, d.id); await load(); onChanged?.(); }
    catch (e) { setError(e.message); }
  };

  const shown = (docs || []).filter((d) => (filter === 'mine' ? d.mine : filter === 'others' ? !d.mine : true));
  const byOrg = {};
  for (const d of docs || []) byOrg[d.org?.name || 'Unknown'] = (byOrg[d.org?.name || 'Unknown'] || 0) + 1;

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files); }}
        className={`rounded-[14px] border-2 border-dashed p-6 mb-4 text-center transition-all ${dragging ? 'border-accent-light bg-accent-glow' : 'border-strong bg-elevated'}`}
      >
        <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => upload(e.target.files)} disabled={uploading} />
        <div className="flex flex-col items-center gap-2">
          {uploading ? <Loader2 size={18} className="animate-spin text-accent-light" /> : <Upload size={18} className="text-accent-light" />}
          <div className="text-[13px] font-semibold text-primary-c">{uploading ? 'Uploading…' : 'Drop files here, or'}</div>
          {!uploading && (
            <button onClick={() => fileRef.current?.click()} className="px-4 py-2 rounded-[10px] text-[12.5px] font-semibold text-white gradient-accent shadow-glow">Choose files</button>
          )}
          <div className="text-[11.5px] text-muted-c max-w-[520px]">
            Records, imaging reports, itemized bills, EOBs, crash reports, authorizations. Everything uploaded here is visible to every organization on this case.
          </div>
        </div>
      </div>

      {error && <div className="text-[12px] text-danger bg-danger-soft rounded-[10px] px-3.5 py-2.5 mb-4">{error}</div>}

      <div className="bg-elevated border border-theme rounded-[14px] overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-theme flex-wrap">
          <div className="flex gap-0.5 bg-tertiary p-1 rounded-[10px]">
            {[['all', `All (${(docs || []).length})`], ['mine', 'Uploaded by us'], ['others', 'From others']].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} className={`px-3 py-[6px] rounded-[7px] text-[12px] font-semibold transition-all ${filter === v ? 'bg-accent text-white shadow-glow' : 'text-muted-c hover:text-primary-c'}`}>{l}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap text-[11px] text-muted-c">
            {Object.entries(byOrg).map(([name, n]) => (
              <span key={name} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-tertiary"><Building2 size={10} /> {name} · {n}</span>
            ))}
          </div>
        </div>
        {docs === null ? (
          <div className="px-4 py-8 text-[12.5px] text-muted-c flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading documents…</div>
        ) : shown.length === 0 ? (
          <div className="px-4 py-10 text-center text-[12.5px] text-muted-c">No documents {filter === 'all' ? 'on this case yet' : 'in this view'}.</div>
        ) : (
          /* Fixed layout so long file names truncate inside the Document
             column instead of widening the table past a phone screen; the
             From / Uploaded / Size columns only exist from md up, and the
             phone row shows the uploader under the name instead. */
          <table className="w-full table-fixed">
            <thead>
              <tr className="border-b border-theme">
                {[['Document', ''], ['From', 'hidden md:table-cell md:w-[200px]'], ['Uploaded', 'hidden md:table-cell md:w-[150px]'], ['Size', 'hidden md:table-cell md:w-[80px]'], ['', 'w-[72px]']].map(([h, w], i) => (
                  <th key={h || i} className={`text-[10.5px] font-semibold text-muted-c uppercase tracking-wider px-4 py-2.5 text-left ${w}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {shown.map((d) => (
                <tr key={d.id} className="hover:bg-hover-surface transition-all">
                  <td className="px-4 py-3">
                    <button onClick={() => open(d)} className="flex items-center gap-2.5 text-left group min-w-0 w-full">
                      <div className="w-8 h-8 rounded-[8px] bg-accent-glow text-accent-light flex items-center justify-center shrink-0"><FileText size={14} /></div>
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium text-primary-c group-hover:text-accent-light truncate">{d.name}</div>
                        {d.description && <div className="text-[11px] text-muted-c truncate">{d.description}</div>}
                        <div className="md:hidden text-[10.5px] text-muted-c truncate">
                          {d.org?.name}{d.mine ? ' (you)' : ''}{d.uploader ? ` · ${d.uploader.first_name} ${d.uploader.last_name}` : ''} · {fmtDT(d.created_at)}
                        </div>
                      </div>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-[12px] text-secondary-c whitespace-nowrap hidden md:table-cell">
                    {d.org?.name}{d.mine && <span className="ml-1.5 text-[10px] font-semibold text-accent-light">you</span>}
                    {d.uploader && <div className="text-[10.5px] text-muted-c">{d.uploader.first_name} {d.uploader.last_name}</div>}
                  </td>
                  <td className="px-4 py-3 text-[12px] text-secondary-c whitespace-nowrap hidden md:table-cell">{fmtDT(d.created_at)}</td>
                  <td className="px-4 py-3 text-[12px] text-muted-c whitespace-nowrap hidden md:table-cell">{fmtSize(d.size_bytes)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => open(d)} className="text-muted-c hover:text-accent-light mr-3" title="Open"><Download size={14} /></button>
                    {d.mine && <button onClick={() => remove(d)} className="text-muted-c hover:text-danger" title="Delete"><Trash2 size={14} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
